/**
 * Nova runs the same codebase as the mainline, with its own behaviour behind this switch.
 *
 * It is derived from the environment name rather than carried as its own variable, so it cannot
 * disagree with where the code is actually deployed. A separate `IS_NOVA` secret could be set to
 * false on a Nova environment and nothing would notice.
 *
 * The backend reads `env.ENV` and the frontend reads `config.env`. Those two vocabularies differ
 * elsewhere — the backend says `dev` and `prod` where the frontend says `development` and
 * `production` — but they agree on exactly these two values, which is all this needs.
 *
 * Verified against the deployed secrets on 2026-08-21: `fuze-dev-nova` holds `ENV=dev-nova` and
 * `fuze-qa-nova` holds `ENV=qa-nova`.
 *
 * `pp` is pre-prod, switched to Nova in place rather than given a separate `pp-nova` environment.
 * It is the one Nova name without the `-nova` suffix, so anything else keyed off this list must
 * name it explicitly rather than match a glob.
 *
 * `local-nova` is for running the Nova variant on a developer machine against a local copy of a
 * Nova database. No deployed secret holds it, and the deploy workflows set these values explicitly,
 * so it cannot reach a deployed environment.
 *
 * The helm migrate job repeats this list in shell (`helm/templates/migrate-job.yaml`). Change both.
 */
export const NOVA_ENVS = ['dev-nova', 'qa-nova', 'local-nova', 'pp'] as const

export type NovaEnv = (typeof NOVA_ENVS)[number]

export function isNovaEnv(environment: string | undefined | null): boolean {
  return NOVA_ENVS.includes(environment as NovaEnv)
}

/**
 * Nova's journey vocabulary, alongside `JOURNEY_STEPS` in ./orders rather than replacing it.
 *
 * The two disagree about names, not only membership: in the legacy feed that mainline reads,
 * CARRIER means what this vocabulary calls DISPATCH, and ONROUTE means what this calls CARRIER.
 * Folding them together would put two different meanings behind one label, so mainline keeps its
 * own steps and its own translation keys until the cutover retires them.
 *
 * These live in a package rather than the backend's nova module because the frontend renders
 * them.
 */
export const NOVA_JOURNEY_STEPS = [
  'RECEIVED',
  'SUBMITTED',
  'CONFIRMED',
  'DISPATCH',
  'CARRIER',
  'DELIVERED',
  'INVOICED',
] as const

export type NovaJourneyStep = (typeof NOVA_JOURNEY_STEPS)[number]

/**
 * `fz_account.fulfillment_method`. An account set to this never ships its two legs together, so
 * its coupled orders are decoupled from the start, before anyone touches them. The Nova orders
 * view hardcodes the same literal in SQL, so the two must change together.
 */
export const FULFILLMENT_METHOD_SEPARATE_ORDERS = 'Separate Orders'

/**
 * Sentinel `decoupledReason` for an order decoupled by its account's fulfillment method rather
 * than by a person. Distinguishable from the free text someone types in the decouple dialog, so
 * the UI can render a translated sentence instead of the raw value.
 */
export const DECOUPLED_REASON_FULFILLMENT_METHOD = 'FULFILLMENT_METHOD_SEPARATE_ORDERS'

/**
 * The Dispatch/Carrier boundary for box status codes. Dispatch owns codes up to and including
 * this, plus those in `DISPATCH_CODES_ABOVE_BOUNDARY`, so any SQL using the boundary must
 * exclude those as well.
 */
export const DISPATCH_MAX_STATUS_CODE = 125

/** Codes belonging to Dispatch despite being numerically above the boundary. */
export const DISPATCH_CODES_ABOVE_BOUNDARY = [301] as const

export const ORDER_SUBSTATUS_LABELS = {
  RECEIVED: 'orderSubstatus.received',
  SUBMITTED: 'orderSubstatus.submitted',
  CONFIRMED: 'orderSubstatus.confirmed',
  DISPATCH: 'orderSubstatus.dispatch',
  CARRIER: 'orderSubstatus.carrier',
  // The box roll-up gives a richer heading when there is box data ("3 boxes delivered"), and this
  // is what the fall-through uses when there is none. Without it a delivered order with no boxes
  // skipped straight past DELIVERED to CARRIER and read "Sent to Ryder", which is where it had
  // been rather than where it got to. Returns carry no boxes at all.
  DELIVERED: 'orderSubstatus.delivered',
  INVOICED: 'orderSubstatus.invoiced',
} as const

/** Returns the i18n key for a box status code, or null if unknown */
export function getBoxStatusLabel(statusCode: number | null): string | null {
  if (statusCode === null) return null
  return `shipment_status.${statusCode}.title`
}

/**
 * Statuses that indicate the order/leg has reached the carrier-or-later stage.
 */
export const CARRIER_OR_LATER_STATUSES = new Set([
  'CARRIER',
  'DELIVERED',
  'INVOICED',
  'INVOICED WITH EXCEPTION',
])

export function isAtCarrierStage(status: string | null | undefined): boolean {
  return !!status && CARRIER_OR_LATER_STATUSES.has(status)
}

/**
 * DELIVERY STATUS CODES (fz_delivery_status.status_code)
 *
 * Segment and colour come from the explicit per-code table below rather than
 * range math, so the returns codes stay unambiguous. Source of truth:
 * `Fuze Project/nova-shipment-status-codes.csv` and the "Order journey /
 * progress-bar rules" section of `functionality/order-to-cash-flow.md`.
 *
 * - DISPATCH owns the warehouse-prep codes 100 to 125, PLUS the returns code
 *   301 (NotPicked). 301 sits out of numeric order on purpose: the legacy
 *   tracking page classes it as early `processing` with positive sentiment, and
 *   a return that has not been picked up yet has not reached a carrier. This is
 *   why segment comes from the table below and not from range math.
 * - CARRIER owns everything past 125 that is not final, EXCEPT 301.
 * - DELIVERED is the final set {200, 205, 210, 225, 302}. A leg stays at
 *   Carrier until ALL of its boxes reach a final code; there is no separate
 *   partial-delivery stage.
 * - Colour is per code and aggregates worst-case per leg (red > yellow > green).
 */
export const BOX_SEGMENTS = ['DISPATCH', 'CARRIER', 'DELIVERED'] as const
export type BoxSegment = (typeof BOX_SEGMENTS)[number]

export const BOX_STATUS_TONES = ['GREEN', 'YELLOW', 'RED'] as const
export type BoxStatusTone = (typeof BOX_STATUS_TONES)[number]

/**
 * The Dispatch/Carrier boundary. Dispatch owns codes up to and including this — and additionally 301,
 * which is why any SQL using this boundary must also exclude `DISPATCH_CODES_ABOVE_BOUNDARY`.
 */

/**
 * Codes that belong to DISPATCH despite being numerically above the boundary. Exists so the SQL that
 * cannot read `BOX_STATUS_CODE_META` still agrees with it.
 */

export const BOX_STATUS_CODE_META: Record<number, { segment: BoxSegment; tone: BoxStatusTone }> = {
  100: { segment: 'DISPATCH', tone: 'GREEN' }, // Accepted
  105: { segment: 'DISPATCH', tone: 'GREEN' }, // Routed
  110: { segment: 'DISPATCH', tone: 'GREEN' }, // Planned
  115: { segment: 'DISPATCH', tone: 'GREEN' }, // Picking
  120: { segment: 'DISPATCH', tone: 'GREEN' }, // Picked
  125: { segment: 'DISPATCH', tone: 'GREEN' }, // ReadytoShip
  130: { segment: 'CARRIER', tone: 'GREEN' }, // Shipped
  135: { segment: 'CARRIER', tone: 'GREEN' }, // Courier
  140: { segment: 'CARRIER', tone: 'GREEN' }, // Estimated
  145: { segment: 'CARRIER', tone: 'GREEN' }, // Transit
  150: { segment: 'CARRIER', tone: 'GREEN' }, // Attempted
  200: { segment: 'DELIVERED', tone: 'GREEN' }, // Delivered
  203: { segment: 'CARRIER', tone: 'YELLOW' }, // Return_To_Sender
  205: { segment: 'DELIVERED', tone: 'RED' }, // Delivered_To_Site (carrier returned shipment)
  210: { segment: 'DELIVERED', tone: 'RED' }, // Refused
  215: { segment: 'CARRIER', tone: 'YELLOW' }, // Undelivered
  220: { segment: 'CARRIER', tone: 'YELLOW' }, // Delayed
  225: { segment: 'DELIVERED', tone: 'RED' }, // Cancelled
  301: { segment: 'DISPATCH', tone: 'GREEN' }, // NotPicked (returns) — see the note above
  302: { segment: 'DELIVERED', tone: 'GREEN' }, // Processed (returns)
}

/**
 * Unknown codes fall back to CARRIER so a box with an unrecognised status keeps
 * the leg in flight rather than silently completing the Delivered step.
 */
export function getBoxSegment(code: number | null): BoxSegment | null {
  if (code === null) return null
  return BOX_STATUS_CODE_META[code]?.segment ?? 'CARRIER'
}

export function getBoxTone(code: number | null): BoxStatusTone | null {
  if (code === null) return null
  return BOX_STATUS_CODE_META[code]?.tone ?? 'GREEN'
}

/** A box is still in warehouse prep (100 to 125). */
export function isDispatchCode(code: number | null): boolean {
  return getBoxSegment(code) === 'DISPATCH'
}

/** A box has left the warehouse but has not reached a final state. */
export function isCarrierCode(code: number | null): boolean {
  return getBoxSegment(code) === 'CARRIER'
}

/** A box has reached a final state: delivered, returned, refused, cancelled or processed. */
export function isDeliveredCode(code: number | null): boolean {
  return getBoxSegment(code) === 'DELIVERED'
}

/**
 * Codes worth surfacing as an attention note: the yellow carrier problems
 * (203, 215, 220) and the red final states (205, 210, 225). Equivalently, every
 * code whose tone is not GREEN.
 *
 * 301 (NotPicked) is deliberately absent. It was here while it counted as a
 * yellow CARRIER code, but it is a green DISPATCH code as of 2026-08-14 — the
 * legacy tracking page treats it as ordinary early processing with positive
 * sentiment, so a return awaiting pickup is not an exception.
 */
export const EXCEPTION_STATUS_CODES = [203, 205, 210, 215, 220, 225] as const
export type ExceptionStatusCode = (typeof EXCEPTION_STATUS_CODES)[number]

export function isExceptionCode(code: number | null): code is ExceptionStatusCode {
  return code !== null && (EXCEPTION_STATUS_CODES as readonly number[]).includes(code)
}

/** Worst-case tone across a set of boxes: red beats yellow beats green. */
export function aggregateBoxTone(codes: (number | null)[]): BoxStatusTone {
  const tones = codes.map(getBoxTone).filter((tone): tone is BoxStatusTone => tone !== null)
  if (tones.includes('RED')) return 'RED'
  if (tones.includes('YELLOW')) return 'YELLOW'
  return 'GREEN'
}

/**
 * Nova's journey vocabulary has its own labels and its own document-number map, because the step
 * set differs from mainline's: it drops ONROUTE and adds DISPATCH and DELIVERED.
 */
export const NOVA_JOURNEY_STEPS_LABELS: Record<NovaJourneyStep, string> = {
  RECEIVED: 'journeyStepLabel.received',
  SUBMITTED: 'journeyStepLabel.submitted',
  CONFIRMED: 'journeyStepLabel.confirmed',
  DISPATCH: 'journeyStepLabel.dispatch',
  CARRIER: 'journeyStepLabel.carrier',
  DELIVERED: 'journeyStepLabel.delivered',
  INVOICED: 'journeyStepLabel.invoiced',
}

export const getNovaJourneyStepLabel = (step: NovaJourneyStep) => NOVA_JOURNEY_STEPS_LABELS[step]

export type NovaJourneyStepWithDocument = Exclude<NovaJourneyStep, 'RECEIVED' | 'SUBMITTED'>

const NOVA_DOCUMENT_NUMBER_LABELS: Record<NovaJourneyStepWithDocument, string> = {
  CONFIRMED: 'documentNumberLabel.sapOrderNumber',
  DISPATCH: 'documentNumberLabel.sapDeliveryNumber',
  CARRIER: 'documentNumberLabel.sapDeliveryNumber',
  DELIVERED: 'documentNumberLabel.sapDeliveryNumber',
  INVOICED: 'documentNumberLabel.sapInvoiceNumber',
}

export const getNovaDocumentNumberLabel = (step: NovaJourneyStepWithDocument) =>
  NOVA_DOCUMENT_NUMBER_LABELS[step] ?? 'documentNumberLabel.generic'
